/**
 * CategoryAEOViewer Component
 *
 * Reusable viewer for Category AEO content sections.
 * Displays all AEO sections with proper formatting, product images, and styling.
 *
 * Used in:
 * - CategoryLeafDetail.tsx (content creation flow)
 * - ReviewPanel.tsx (content review dashboard)
 */

import React, { useState } from 'react';
import {
  CheckCircle,
  Target,
  Users,
  Sparkles,
  TrendingUp,
  ShoppingBag,
  ChevronRight,
  FileText,
  Link2,
  Copy,
  AlertTriangle,
  BookOpen,
  ListChecks,
  DollarSign,
  Trophy,
  ArrowLeftRight,
  Star,
  ThumbsUp,
  ThumbsDown,
  Calendar,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { humanizeKey } from '@/utils/humanize-key';

interface CategoryAEOSections {
  featured_snippet?: string;
  category_purpose?: string;
  buyer_guide?: {
    intro?: string;
    rules?: Array<{ profile: string; recommendation: string }>;
  };
  differentiators?: string[];
  use_cases?: Array<{
    use_case: string;
    description: string;
    recommended_product?: {
      image_url?: string;
      title: string;
      brand: string;
      price: number;
    };
    why_recommended?: string;
  }>;
  top_picks?: Array<{
    image_url?: string;
    title: string;
    brand: string;
    price: number;
    ai_summary?: string;
    quick_facts?: {
      top_features?: string[];
    };
  }>;
  personas?: Array<{
    name: string;
    summary: string;
    needs?: string[];
    recommended_products?: Array<{
      image_url?: string;
      title: string;
      brand: string;
      price: number;
    }>;
  }>;
  price_tiers?: Array<{
    name: string;
    label?: string;
    minPrice?: number;
    maxPrice?: number;
    priceDisplay?: string;
    productCount?: number;
    topPick?: {
      image_url?: string;
      title: string;
      price: number;
    };
  }>;
  subcategory_taxonomy?: Array<{
    name: string;
    product_count?: number;
    suggested?: boolean;
  }>;
  faqs?: Array<{
    question: string;
    answer: string;
  }>;
  knowledge_graph?: {
    parent_category?: string;
    sibling_categories?: Array<{ name: string }>;
    featured_brands?: string[];
  };
  narrative?: string;
  ai_context_summary?: string | Record<string, unknown>;
  best_sellers?: Array<{
    image_url?: string;
    title: string;
    brand: string;
    price: number;
  }>;
  // Buying guide sections
  title?: string;
  introduction?: string;
  key_factors?: Array<{
    factor: string;
    importance?: string;
    explanation: string;
    what_to_look_for?: string;
    budget_recommendation?: string;
    premium_recommendation?: string;
  }>;
  buyer_profiles?: Array<{
    profile: string;
    key_advice: string;
    priorities?: string[];
    budget_range?: string;
    recommended_brands?: string[];
  }>;
  common_mistakes?: string[];
  price_tier_guide?: Record<string, {
    range: string;
    best_for: string;
    what_to_expect: string;
  }>;
  decision_checklist?: string[];
  // Comparison page sections
  comparison_table?: Array<{
    attribute: string;
    display_name?: string;
    entity_a: string;
    entity_b: string;
    winner?: string;
  }>;
  verdict_by_use_case?: Array<{
    use_case: string;
    winner: string;
    reason: string;
  }>;
  price_comparison?: {
    entity_a_avg: number;
    entity_b_avg: number;
    value_winner: string;
  };
  summary?: string;
  recommendation?: string;
  // Best-of page sections
  last_updated?: string;
  methodology?: string;
  rankings?: Array<{
    rank: number;
    product_title: string;
    brand: string;
    price: number | string;
    award?: string;
    why_chosen: string;
    pros?: string[];
    cons?: string[];
    best_for?: string;
    key_specs?: string;
  }>;
  budget_pick?: { title: string; price: string | number; why: string };
  premium_pick?: { title: string; price: string | number; why: string };
  verdict?: string;
}

interface CategoryAEOViewerProps {
  sections: CategoryAEOSections;
  showCopyButtons?: boolean;
  onCopySection?: (title: string, content: string) => void;
  className?: string;
}

export function CategoryAEOViewer({
  sections,
  showCopyButtons = false,
  onCopySection,
  className = '',
}: CategoryAEOViewerProps) {
  const [copiedSection, setCopiedSection] = useState<string | null>(null);

  const handleCopy = (title: string, content: string) => {
    navigator.clipboard.writeText(content);
    setCopiedSection(title);
    setTimeout(() => setCopiedSection(null), 2000);
    onCopySection?.(title, content);
  };

  if (!sections) return null;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Featured Snippet */}
      {sections.featured_snippet && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-purple-700 dark:text-purple-300 flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Featured Snippet
            </h3>
            {showCopyButtons && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleCopy('Featured Snippet', sections.featured_snippet!)}
              >
                {copiedSection === 'Featured Snippet' ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            )}
          </div>
          <div className="bg-purple-50 dark:bg-purple-950/30 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
            <p className="text-sm">{sections.featured_snippet}</p>
          </div>
        </div>
      )}

      {/* Category Purpose */}
      {sections.category_purpose && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2">
              <Target className="h-4 w-4 text-blue-600" />
              Category Purpose
            </h3>
            {showCopyButtons && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleCopy('Category Purpose', sections.category_purpose!)}
              >
                {copiedSection === 'Category Purpose' ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            )}
          </div>
          <div className="bg-blue-50 dark:bg-blue-950/30 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-sm whitespace-pre-wrap">{sections.category_purpose}</p>
          </div>
        </div>
      )}

      {/* Buyer Guide */}
      {sections.buyer_guide && (
        <div className="space-y-2">
          <h3 className="font-semibold flex items-center gap-2">
            <Users className="h-4 w-4 text-green-600" />
            Buyer Guide
          </h3>
          <div className="bg-green-50 dark:bg-green-950/30 p-4 rounded-lg border border-green-200 dark:border-green-800">
            {sections.buyer_guide.intro && (
              <p className="font-medium mb-3">{sections.buyer_guide.intro}</p>
            )}
            <div className="space-y-2">
              {sections.buyer_guide.rules?.map((rule, idx) => (
                <div key={idx} className="flex gap-2 text-sm">
                  <span className="text-green-600 font-medium">•</span>
                  <div>
                    <span className="font-medium">{rule.profile}</span>
                    <span className="text-muted-foreground"> → {rule.recommendation}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Differentiators */}
      {sections.differentiators && sections.differentiators.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-semibold flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-600" />
            Key Differentiators
          </h3>
          <div className="bg-amber-50 dark:bg-amber-950/30 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
            <ul className="space-y-2">
              {sections.differentiators.map((diff, idx) => (
                <li key={idx} className="text-sm flex gap-2">
                  <span className="text-amber-600">✦</span>
                  {diff}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Use Cases */}
      {sections.use_cases && sections.use_cases.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-semibold flex items-center gap-2">
            <Target className="h-4 w-4 text-emerald-600" />
            Best For... (Use Case Recommendations)
          </h3>
          <div className="bg-emerald-50 dark:bg-emerald-950/30 p-4 rounded-lg border border-emerald-200 dark:border-emerald-800">
            <div className="grid gap-4">
              {sections.use_cases.map((useCase, idx) => (
                <div key={idx} className="border-b border-emerald-200 dark:border-emerald-700 pb-3 last:border-0 last:pb-0">
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <p className="font-semibold text-emerald-800 dark:text-emerald-200">{useCase.use_case}</p>
                      <p className="text-xs text-muted-foreground mb-2">{useCase.description}</p>
                      {useCase.recommended_product && (
                        <div className="bg-white dark:bg-emerald-900/50 p-2 rounded flex items-center gap-3">
                          {useCase.recommended_product.image_url && (
                            <img
                              src={useCase.recommended_product.image_url}
                              alt={useCase.recommended_product.title}
                              className="w-12 h-12 object-cover rounded"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{useCase.recommended_product.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {useCase.recommended_product.brand} • {useCase.recommended_product.price}€
                            </p>
                          </div>
                        </div>
                      )}
                      {useCase.why_recommended && (
                        <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1 italic">{useCase.why_recommended}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Top Picks */}
      {sections.top_picks && sections.top_picks.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-semibold flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-orange-600" />
            Top Picks (AI-Ready Summaries)
          </h3>
          <div className="bg-orange-50 dark:bg-orange-950/30 p-4 rounded-lg border border-orange-200 dark:border-orange-800">
            <div className="grid gap-4">
              {sections.top_picks.slice(0, 5).map((pick, idx) => (
                <div key={idx} className="border-b border-orange-200 dark:border-orange-700 pb-3 last:border-0 last:pb-0">
                  <div className="flex items-start gap-3">
                    {pick.image_url && (
                      <img
                        src={pick.image_url}
                        alt={pick.title}
                        className="w-16 h-16 object-cover rounded"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-xs">{idx + 1}</Badge>
                        <p className="text-sm font-medium truncate">{pick.title}</p>
                      </div>
                      <p className="text-xs text-muted-foreground mb-1">
                        {pick.brand} • {pick.price}€
                      </p>
                      {pick.ai_summary && (
                        <p className="text-xs text-orange-700 dark:text-orange-300 bg-orange-100 dark:bg-orange-900/50 p-2 rounded italic">
                          "{pick.ai_summary}"
                        </p>
                      )}
                      {pick.quick_facts?.top_features && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {pick.quick_facts.top_features.slice(0, 3).map((feature, fidx) => (
                            <Badge key={fidx} variant="secondary" className="text-[10px]">{feature}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Personas */}
      {sections.personas && sections.personas.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-semibold flex items-center gap-2">
            <Users className="h-4 w-4 text-pink-600" />
            Recommendations by User Type
          </h3>
          <div className="bg-pink-50 dark:bg-pink-950/30 p-4 rounded-lg border border-pink-200 dark:border-pink-800">
            <div className="grid gap-4">
              {sections.personas.slice(0, 5).map((persona, idx) => (
                <div key={idx} className="border-b border-pink-200 dark:border-pink-700 pb-3 last:border-0 last:pb-0">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-pink-200 dark:bg-pink-800 flex items-center justify-center text-pink-700 dark:text-pink-200 font-bold text-sm">
                      {persona.name?.charAt(0) || '?'}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-pink-800 dark:text-pink-200">{persona.name}</p>
                      <p className="text-xs text-muted-foreground mb-2">{persona.summary}</p>
                      {persona.needs && persona.needs.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {persona.needs.slice(0, 3).map((need, nidx) => (
                            <Badge key={nidx} variant="outline" className="text-[10px]">{need}</Badge>
                          ))}
                        </div>
                      )}
                      {persona.recommended_products && persona.recommended_products.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-pink-600">Recommended:</p>
                          {persona.recommended_products.slice(0, 2).map((prod, pidx) => (
                            <div key={pidx} className="bg-white dark:bg-pink-900/50 p-2 rounded flex items-center gap-2">
                              {prod.image_url && (
                                <img src={prod.image_url} alt={prod.title} className="w-8 h-8 object-cover rounded" />
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium truncate">{prod.title}</p>
                                <p className="text-[10px] text-muted-foreground">{prod.brand} • {prod.price}€</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Price Tiers */}
      {sections.price_tiers && sections.price_tiers.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-semibold flex items-center gap-2">
            <ShoppingBag className="h-4 w-4 text-yellow-600" />
            Price Tier Recommendations
          </h3>
          <div className="bg-yellow-50 dark:bg-yellow-950/30 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
            <div className="grid grid-cols-2 gap-3">
              {sections.price_tiers.map((tier, idx) => (
                <div key={idx} className="bg-white dark:bg-yellow-900/50 p-3 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className={
                      tier.name === 'budget' ? 'bg-green-100 text-green-800' :
                      tier.name === 'mid_range' ? 'bg-blue-100 text-blue-800' :
                      tier.name === 'premium' ? 'bg-purple-100 text-purple-800' :
                      'bg-yellow-100 text-yellow-800'
                    }>
                      {tier.label || tier.name}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {tier.priceDisplay || `${tier.minPrice}-${tier.maxPrice}€`}
                    </span>
                  </div>
                  {tier.productCount !== undefined && (
                    <p className="text-xs text-muted-foreground mb-1">{tier.productCount} products</p>
                  )}
                  {tier.topPick && (
                    <div className="flex items-center gap-2 mt-2">
                      {tier.topPick.image_url && (
                        <img src={tier.topPick.image_url} alt={tier.topPick.title} className="w-10 h-10 object-cover rounded" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">{tier.topPick.title}</p>
                        <p className="text-[10px] text-muted-foreground">{tier.topPick.price}€</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Subcategory Taxonomy */}
      {sections.subcategory_taxonomy && sections.subcategory_taxonomy.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-semibold flex items-center gap-2">
            <ChevronRight className="h-4 w-4 text-slate-600" />
            Subcategory Taxonomy
          </h3>
          <div className="bg-slate-50 dark:bg-slate-950/30 p-4 rounded-lg border border-slate-200 dark:border-slate-800">
            <div className="flex flex-wrap gap-2">
              {sections.subcategory_taxonomy.map((sub, idx) => (
                <Badge key={idx} variant={sub.suggested ? 'secondary' : 'default'} className="text-xs">
                  {sub.name}
                  {sub.product_count !== undefined && sub.product_count > 0 && (
                    <span className="ml-1 opacity-60">({sub.product_count})</span>
                  )}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* FAQs */}
      {sections.faqs && sections.faqs.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2">
              <FileText className="h-4 w-4 text-indigo-600" />
              Frequently Asked Questions ({sections.faqs.length})
            </h3>
            {showCopyButtons && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleCopy('FAQs', sections.faqs!.map(f => `Q: ${f.question}\nA: ${f.answer}`).join('\n\n'))}
              >
                {copiedSection === 'FAQs' ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            )}
          </div>
          <div className="space-y-3">
            {sections.faqs.map((faq, idx) => (
              <div key={idx} className="bg-indigo-50 dark:bg-indigo-950/30 p-4 rounded-lg border border-indigo-200 dark:border-indigo-800">
                <p className="font-medium text-sm text-indigo-900 dark:text-indigo-100 mb-2">Q: {faq.question}</p>
                <p className="text-sm text-indigo-700 dark:text-indigo-300">A: {faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Introduction (buying_guide) */}
      {sections.introduction && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-blue-600" />
              Introduction
            </h3>
            {showCopyButtons && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleCopy('Introduction', sections.introduction!)}
              >
                {copiedSection === 'Introduction' ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            )}
          </div>
          <div className="bg-blue-50 dark:bg-blue-950/30 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-sm whitespace-pre-wrap">{sections.introduction}</p>
          </div>
        </div>
      )}

      {/* Key Factors (buying_guide) */}
      {sections.key_factors && sections.key_factors.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-semibold flex items-center gap-2">
            <Target className="h-4 w-4 text-emerald-600" />
            Key Factors ({sections.key_factors.length})
          </h3>
          <div className="space-y-3">
            {sections.key_factors.map((factor, idx) => (
              <div key={idx} className="bg-emerald-50 dark:bg-emerald-950/30 p-4 rounded-lg border border-emerald-200 dark:border-emerald-800">
                <div className="flex items-center gap-2 mb-2">
                  <p className="font-semibold text-sm text-emerald-900 dark:text-emerald-100">{factor.factor}</p>
                  {factor.importance && (
                    <Badge variant="outline" className={
                      factor.importance === 'high' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                      factor.importance === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                      'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    }>{factor.importance}</Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-2">{factor.explanation}</p>
                {factor.what_to_look_for && (
                  <p className="text-xs"><span className="font-medium">Look for:</span> {factor.what_to_look_for}</p>
                )}
                <div className="flex gap-4 mt-2">
                  {factor.budget_recommendation && (
                    <p className="text-xs flex-1"><span className="font-medium text-green-700 dark:text-green-400">Budget:</span> {factor.budget_recommendation}</p>
                  )}
                  {factor.premium_recommendation && (
                    <p className="text-xs flex-1"><span className="font-medium text-purple-700 dark:text-purple-400">Premium:</span> {factor.premium_recommendation}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Buyer Profiles (buying_guide) */}
      {sections.buyer_profiles && sections.buyer_profiles.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-semibold flex items-center gap-2">
            <Users className="h-4 w-4 text-pink-600" />
            Buyer Profiles ({sections.buyer_profiles.length})
          </h3>
          <div className="space-y-3">
            {sections.buyer_profiles.map((profile, idx) => (
              <div key={idx} className="bg-pink-50 dark:bg-pink-950/30 p-4 rounded-lg border border-pink-200 dark:border-pink-800">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-pink-200 dark:bg-pink-800 flex items-center justify-center text-pink-700 dark:text-pink-200 font-bold text-sm">
                    {profile.profile?.charAt(0) || '?'}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-pink-800 dark:text-pink-200">{profile.profile}</p>
                    {profile.budget_range && (
                      <Badge variant="outline" className="text-xs mt-1">{profile.budget_range} budget</Badge>
                    )}
                    <p className="text-sm text-muted-foreground mt-2">{profile.key_advice}</p>
                    {profile.priorities && profile.priorities.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {profile.priorities.map((p, pidx) => (
                          <Badge key={pidx} variant="secondary" className="text-[10px]">{p}</Badge>
                        ))}
                      </div>
                    )}
                    {profile.recommended_brands && profile.recommended_brands.length > 0 && (
                      <p className="text-xs mt-2"><span className="font-medium">Brands:</span> {profile.recommended_brands.join(', ')}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Price Tier Guide (buying_guide) */}
      {sections.price_tier_guide && typeof sections.price_tier_guide === 'object' && (
        <div className="space-y-2">
          <h3 className="font-semibold flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-yellow-600" />
            Price Tier Guide
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {Object.entries(sections.price_tier_guide).map(([tierName, tier]) => (
              <div key={tierName} className="bg-yellow-50 dark:bg-yellow-950/30 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className={
                    tierName === 'budget' ? 'bg-green-100 text-green-800' :
                    tierName === 'mid_range' ? 'bg-blue-100 text-blue-800' :
                    'bg-purple-100 text-purple-800'
                  }>{tierName.replace('_', ' ')}</Badge>
                  <span className="text-xs font-medium">{tier.range}</span>
                </div>
                <p className="text-xs font-medium text-muted-foreground">{tier.best_for}</p>
                <p className="text-xs mt-1">{tier.what_to_expect}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Common Mistakes (buying_guide) */}
      {sections.common_mistakes && sections.common_mistakes.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-semibold flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            Common Mistakes to Avoid
          </h3>
          <div className="bg-red-50 dark:bg-red-950/30 p-4 rounded-lg border border-red-200 dark:border-red-800">
            <ul className="space-y-2">
              {sections.common_mistakes.map((mistake, idx) => (
                <li key={idx} className="text-sm flex gap-2">
                  <span className="text-red-500">✗</span>
                  {mistake}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Decision Checklist (buying_guide) */}
      {sections.decision_checklist && sections.decision_checklist.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-semibold flex items-center gap-2">
            <ListChecks className="h-4 w-4 text-teal-600" />
            Decision Checklist
          </h3>
          <div className="bg-teal-50 dark:bg-teal-950/30 p-4 rounded-lg border border-teal-200 dark:border-teal-800">
            <ul className="space-y-2">
              {sections.decision_checklist.map((item, idx) => (
                <li key={idx} className="text-sm flex gap-2">
                  <span className="text-teal-600">☐</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Comparison Table (comparison page) */}
      {sections.comparison_table && sections.comparison_table.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-semibold flex items-center gap-2">
            <ArrowLeftRight className="h-4 w-4 text-blue-600" />
            Comparison Table
          </h3>
          <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-blue-100 dark:bg-blue-900/50">
                  <th className="text-left p-3 font-medium">Attribute</th>
                  <th className="text-left p-3 font-medium">Option A</th>
                  <th className="text-left p-3 font-medium">Option B</th>
                  <th className="text-center p-3 font-medium">Winner</th>
                </tr>
              </thead>
              <tbody>
                {sections.comparison_table.map((row, idx) => (
                  <tr key={idx} className="border-t border-blue-200 dark:border-blue-800">
                    <td className="p-3 font-medium">{row.display_name || humanizeKey(row.attribute)}</td>
                    <td className="p-3">{row.entity_a}</td>
                    <td className="p-3">{row.entity_b}</td>
                    <td className="p-3 text-center">
                      <Badge variant="outline" className={
                        row.winner === 'tie' ? 'bg-gray-100 text-gray-700' : 'bg-green-100 text-green-800'
                      }>{row.winner || '-'}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Verdict by Use Case (comparison page) */}
      {sections.verdict_by_use_case && sections.verdict_by_use_case.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-semibold flex items-center gap-2">
            <Target className="h-4 w-4 text-emerald-600" />
            Verdict by Use Case
          </h3>
          <div className="space-y-3">
            {sections.verdict_by_use_case.map((uc, idx) => (
              <div key={idx} className="bg-emerald-50 dark:bg-emerald-950/30 p-4 rounded-lg border border-emerald-200 dark:border-emerald-800">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-semibold text-sm">{uc.use_case}</p>
                  <Badge variant="secondary">{uc.winner}</Badge>
                </div>
                <p className="text-sm text-muted-foreground">{uc.reason}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Price Comparison (comparison page) */}
      {sections.price_comparison && (
        <div className="space-y-2">
          <h3 className="font-semibold flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-yellow-600" />
            Price Comparison
          </h3>
          <div className="bg-yellow-50 dark:bg-yellow-950/30 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800 grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-xs text-muted-foreground">Option A Avg</p>
              <p className="text-lg font-bold">{sections.price_comparison.entity_a_avg}€</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Value Winner</p>
              <Badge className="mt-1">{sections.price_comparison.value_winner}</Badge>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Option B Avg</p>
              <p className="text-lg font-bold">{sections.price_comparison.entity_b_avg}€</p>
            </div>
          </div>
        </div>
      )}

      {/* Summary (comparison page) */}
      {sections.summary && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2">
              <FileText className="h-4 w-4 text-slate-600" />
              Summary
            </h3>
            {showCopyButtons && (
              <Button variant="ghost" size="sm" onClick={() => handleCopy('Summary', sections.summary!)}>
                {copiedSection === 'Summary' ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            )}
          </div>
          <div className="bg-slate-50 dark:bg-slate-950/30 p-4 rounded-lg border border-slate-200 dark:border-slate-800">
            <p className="text-sm whitespace-pre-wrap">{sections.summary}</p>
          </div>
        </div>
      )}

      {/* Recommendation (comparison page) */}
      {sections.recommendation && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2">
              <Star className="h-4 w-4 text-amber-600" />
              Recommendation
            </h3>
            {showCopyButtons && (
              <Button variant="ghost" size="sm" onClick={() => handleCopy('Recommendation', sections.recommendation!)}>
                {copiedSection === 'Recommendation' ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            )}
          </div>
          <div className="bg-amber-50 dark:bg-amber-950/30 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
            <p className="text-sm whitespace-pre-wrap">{sections.recommendation}</p>
          </div>
        </div>
      )}

      {/* Rankings (best_of page) */}
      {sections.rankings && sections.rankings.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2">
              <Trophy className="h-4 w-4 text-amber-600" />
              Rankings ({sections.rankings.length})
            </h3>
            {sections.last_updated && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3" /> Updated: {sections.last_updated}
              </span>
            )}
          </div>
          <div className="space-y-3">
            {sections.rankings.map((item, idx) => (
              <div key={idx} className="bg-amber-50 dark:bg-amber-950/30 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-200 dark:bg-amber-800 flex items-center justify-center text-amber-800 dark:text-amber-200 font-bold text-lg">
                    {item.rank}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-sm">{item.product_title}</p>
                      <span className="text-xs text-muted-foreground">{item.brand} — {item.price}€</span>
                    </div>
                    {item.award && <Badge variant="secondary" className="text-xs mb-2">{item.award}</Badge>}
                    <p className="text-sm text-muted-foreground mb-2">{item.why_chosen}</p>
                    {item.key_specs && <p className="text-xs mb-2"><span className="font-medium">Key specs:</span> {item.key_specs}</p>}
                    {item.best_for && <p className="text-xs"><span className="font-medium">Best for:</span> {item.best_for}</p>}
                    <div className="flex gap-4 mt-2">
                      {item.pros && item.pros.length > 0 && (
                        <div className="flex-1">
                          {item.pros.map((pro, pidx) => (
                            <p key={pidx} className="text-xs flex items-center gap-1 text-green-700 dark:text-green-400">
                              <ThumbsUp className="h-3 w-3" /> {pro}
                            </p>
                          ))}
                        </div>
                      )}
                      {item.cons && item.cons.length > 0 && (
                        <div className="flex-1">
                          {item.cons.map((con, cidx) => (
                            <p key={cidx} className="text-xs flex items-center gap-1 text-red-700 dark:text-red-400">
                              <ThumbsDown className="h-3 w-3" /> {con}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Methodology (best_of page) */}
      {sections.methodology && (
        <div className="space-y-2">
          <h3 className="font-semibold flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-slate-600" />
            Methodology
          </h3>
          <div className="bg-slate-50 dark:bg-slate-950/30 p-4 rounded-lg border border-slate-200 dark:border-slate-800">
            <p className="text-sm whitespace-pre-wrap">{sections.methodology}</p>
          </div>
        </div>
      )}

      {/* Budget & Premium Picks (best_of page) */}
      {(sections.budget_pick || sections.premium_pick) && (
        <div className="space-y-2">
          <h3 className="font-semibold flex items-center gap-2">
            <ShoppingBag className="h-4 w-4 text-green-600" />
            Editor Picks
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {sections.budget_pick && (
              <div className="bg-green-50 dark:bg-green-950/30 p-4 rounded-lg border border-green-200 dark:border-green-800">
                <Badge variant="outline" className="bg-green-100 text-green-800 mb-2">Budget Pick</Badge>
                <p className="font-semibold text-sm">{sections.budget_pick.title}</p>
                <p className="text-xs text-muted-foreground mb-1">{sections.budget_pick.price}€</p>
                <p className="text-sm">{sections.budget_pick.why}</p>
              </div>
            )}
            {sections.premium_pick && (
              <div className="bg-purple-50 dark:bg-purple-950/30 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
                <Badge variant="outline" className="bg-purple-100 text-purple-800 mb-2">Premium Pick</Badge>
                <p className="font-semibold text-sm">{sections.premium_pick.title}</p>
                <p className="text-xs text-muted-foreground mb-1">{sections.premium_pick.price}€</p>
                <p className="text-sm">{sections.premium_pick.why}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Verdict (best_of / comparison pages) */}
      {sections.verdict && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Verdict
            </h3>
            {showCopyButtons && (
              <Button variant="ghost" size="sm" onClick={() => handleCopy('Verdict', sections.verdict!)}>
                {copiedSection === 'Verdict' ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            )}
          </div>
          <div className="bg-green-50 dark:bg-green-950/30 p-4 rounded-lg border border-green-200 dark:border-green-800">
            <p className="text-sm whitespace-pre-wrap">{sections.verdict}</p>
          </div>
        </div>
      )}

      {/* Knowledge Graph */}
      {sections.knowledge_graph && (
        <div className="space-y-2">
          <h3 className="font-semibold flex items-center gap-2">
            <Link2 className="h-4 w-4 text-cyan-600" />
            Knowledge Graph
          </h3>
          <div className="bg-cyan-50 dark:bg-cyan-950/30 p-4 rounded-lg border border-cyan-200 dark:border-cyan-800">
            {sections.knowledge_graph.parent_category && (
              <div className="mb-3">
                <span className="text-xs font-medium text-muted-foreground">Parent Category:</span>
                <p className="text-sm">{sections.knowledge_graph.parent_category}</p>
              </div>
            )}
            {sections.knowledge_graph.sibling_categories && sections.knowledge_graph.sibling_categories.length > 0 && (
              <div className="mb-3">
                <span className="text-xs font-medium text-muted-foreground">Related Categories:</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {sections.knowledge_graph.sibling_categories.map((cat, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">
                      {cat.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {sections.knowledge_graph.featured_brands && sections.knowledge_graph.featured_brands.length > 0 && (
              <div>
                <span className="text-xs font-medium text-muted-foreground">Featured Brands:</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {sections.knowledge_graph.featured_brands.map((brand, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs">
                      {brand}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Narrative */}
      {sections.narrative && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2">
              <FileText className="h-4 w-4 text-rose-600" />
              Category Narrative
            </h3>
            {showCopyButtons && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleCopy('Narrative', sections.narrative!)}
              >
                {copiedSection === 'Narrative' ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            )}
          </div>
          <div className="bg-rose-50 dark:bg-rose-950/30 p-4 rounded-lg border border-rose-200 dark:border-rose-800 max-h-60 overflow-y-auto">
            <p className="text-sm whitespace-pre-wrap">{sections.narrative}</p>
          </div>
        </div>
      )}

      {/* AI Context Summary - Technical metadata, hidden by default */}
      {sections.ai_context_summary && (() => {
        const ctx = sections.ai_context_summary as Record<string, unknown>;
        return (
          <details className="group">
            <summary className="cursor-pointer list-none">
              <div className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
                <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" />
                <Sparkles className="h-4 w-4 text-violet-600" />
                <span className="text-sm">AI Context Summary</span>
              </div>
            </summary>
            <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
              {/* Basic Info */}
              <div className="bg-slate-50 dark:bg-slate-900/50 p-3 rounded-lg">
                <p className="text-xs font-medium text-muted-foreground mb-2">Category Info</p>
                <p className="font-medium">{String(ctx.category || '')}</p>
                <p className="text-xs text-muted-foreground">{String(ctx.full_path || '')}</p>
                <p className="text-xs mt-1">{String(ctx.product_count ?? '')} products</p>
              </div>

              {/* Pricing */}
              <div className="bg-emerald-50 dark:bg-emerald-900/30 p-3 rounded-lg">
                <p className="text-xs font-medium text-muted-foreground mb-2">Pricing</p>
                {ctx.price_range && typeof ctx.price_range === 'object' && (
                  <p className="font-medium">
                    {String((ctx.price_range as Record<string, unknown>).min)}€ - {String((ctx.price_range as Record<string, unknown>).max)}€
                  </p>
                )}
                <p className="text-xs text-muted-foreground">Avg: {String(ctx.average_price ?? '')}€</p>
              </div>

              {/* Brands */}
              {Array.isArray(ctx.top_brands) && ctx.top_brands.length > 0 && (
                <div className="bg-blue-50 dark:bg-blue-900/30 p-3 rounded-lg">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Top Brands</p>
                  <div className="flex flex-wrap gap-1">
                    {ctx.top_brands.slice(0, 6).map((brand: unknown, i: number) => (
                      <Badge key={i} variant="secondary" className="text-xs capitalize">{String(brand)}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Target Consumers */}
              {Array.isArray(ctx.target_consumers) && ctx.target_consumers.length > 0 && (
                <div className="bg-pink-50 dark:bg-pink-900/30 p-3 rounded-lg">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Target Consumers</p>
                  <div className="flex flex-wrap gap-1">
                    {ctx.target_consumers.slice(0, 4).map((t: unknown, i: number) => (
                      <Badge key={i} variant="outline" className="text-xs">{String(t)}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Key Benefits */}
              {Array.isArray(ctx.key_benefits) && ctx.key_benefits.length > 0 && (
                <div className="col-span-2 bg-amber-50 dark:bg-amber-900/30 p-3 rounded-lg">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Key Benefits</p>
                  <div className="flex flex-wrap gap-1">
                    {ctx.key_benefits.slice(0, 6).map((b: unknown, i: number) => (
                      <Badge key={i} variant="secondary" className="text-xs">{String(b)}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </details>
        );
      })()}
    </div>
  );
}

export default CategoryAEOViewer;
